import { chatFetch } from '../lib/apiClient';

/**
 * Portal-side customer-support chat service. Talks to the portal-gated
 * `/api/portal/chat/*` surface over the shared chat module. This module
 * is REST-only (identity bootstrap, chat start/resume, history
 * catch-up); the realtime path is MQTT — HelpChatWidget lazy-loads
 * mqtt.js and connects to the broker using the identity returned by
 * `getChatIdentity`, publishing sends to the outbox topic and receiving
 * fanout on the inbox topic.
 */

/**
 * Identity-only — per the chat-availability design, the storefront
 * never sees per-rep status. The customer always sees who their
 * assigned rep is (relationship preservation), but team availability
 * is communicated through a SEPARATE tenant-aggregate boolean
 * (`teamAvailable`) on the context response.
 */
export interface PortalChatAssignedRep {
	userId: number;
	displayName: string;
}

export type PortalChatContextDto =
	| { available: false }
	| {
			available: true;
			clientId: number;
			clientName: string | null;
			assignedRep: PortalChatAssignedRep | null;
			/**
			 * Tenant-wide aggregate — true when at least one customer-
			 * facing rep is currently available to take a chat. Drives
			 * the panel's two-branch subtitle copy. Chat creation is
			 * allowed in BOTH cases (never block the launcher); this
			 * flag only controls expectation-setting.
			 */
			teamAvailable: boolean;
	  };

export interface PortalChatConversationDto {
	id: number;
	uuid: string;
	status: 'active' | 'archived' | 'resolved';
	lastMessageAt?: string;
	messageCount: number;
}

export interface PortalChatParticipantDto {
	id: number;
	userId?: number;
	participantType:
		| 'internal_user'
		| 'portal_user'
		| 'external_sms'
		| 'external_email'
		| 'system';
	role: 'owner' | 'admin' | 'member' | 'guest';
	displayName: string;
	email?: string;
	isActive: boolean;
}

export interface PortalChatMessageDto {
	id: number;
	uuid: string;
	conversationId: number;
	senderParticipantId?: number;
	/**
	 * Sender's `workdUserId` — the identity primitive ChatThread uses to
	 * decide right-vs-left alignment. Populated by the backend
	 * `listHistory` enrichment for every non-system message. Null on
	 * system messages.
	 */
	senderWorkdUserId?: number;
	/**
	 * Client-assigned dedup id for the original POST. The widget relies
	 * on this to replace optimistic rows with the server-confirmed copy
	 * instead of rendering both side by side.
	 */
	clientMessageId?: string;
	kind: 'text' | 'file' | 'system' | 'call_event';
	content?: string;
	createdDate: string;
}

/**
 * Fetch the intake state for the widget: does the caller have an
 * active client? Who's their assigned rep? Is that rep online right
 * now? The widget uses this to render "Your rep: Sarah (online)"
 * before the user even starts a chat.
 */
export async function getChatContext(
	token: string,
): Promise<PortalChatContextDto> {
	return chatFetch<PortalChatContextDto>('/context', token);
}

/**
 * Return the caller's currently open chat, if any. Used on widget
 * mount so returning visitors with a live session resume instead of
 * getting a fresh intake form.
 */
export async function getCurrentChat(
	token: string,
): Promise<{ conversation: PortalChatConversationDto | null }> {
	return chatFetch<{ conversation: PortalChatConversationDto | null }>(
		'/current',
		token,
	);
}

/**
 * Start a new support chat. Server derives the client from the
 * active-client context when `clientId` is omitted. Returns both the
 * conversation id and whether a rep was assigned immediately, so the
 * widget can switch to "connected" copy right away on a hot hit.
 */
export async function startChat(
	token: string,
	payload: { clientId?: number; topic?: string },
): Promise<{
	conversationId: number;
	queueEntryId: number;
	immediatelyAssigned: boolean;
}> {
	return chatFetch<{
		conversationId: number;
		queueEntryId: number;
		immediatelyAssigned: boolean;
	}>('/start', token, {
		method: 'POST',
		body: JSON.stringify(payload),
	});
}

/**
 * Paginated message history, oldest→newest.
 *   - `beforeMessageId` pages backward from a known boundary (initial
 *     load, scrollback) — omit to get the most recent page.
 *   - `afterMessageId` returns ONLY messages newer than the given id.
 *     Used for catch-up after a transient MQTT drop so we don't refetch
 *     the entire history on every reconnect.
 */
export async function listMessages(
	token: string,
	conversationId: number,
	options?: {
		beforeMessageId?: number;
		afterMessageId?: number;
		limit?: number;
	},
): Promise<{
	messages: Array<PortalChatMessageDto>;
	participants: Array<PortalChatParticipantDto>;
}> {
	const params = new URLSearchParams();
	if (options?.beforeMessageId != null) {
		params.set('beforeMessageId', String(options.beforeMessageId));
	}
	if (options?.afterMessageId != null) {
		params.set('afterMessageId', String(options.afterMessageId));
	}
	if (options?.limit != null) {
		params.set('limit', String(options.limit));
	}
	const qs = params.toString();
	return chatFetch(
		`/conversations/${conversationId}/messages${qs === '' ? '' : `?${qs}`}`,
		token,
	);
}

export async function resolveChat(
	token: string,
	conversationId: number,
): Promise<void> {
	await chatFetch(`/conversations/${conversationId}/resolve`, token, {
		method: 'POST',
	});
}

export interface PortalChatIdentity {
	principalId: string;
	principalType: 'portal';
	inboxTopic: string;
	outboxTopic: string;
	presenceTopic: string;
	brokerUrl: string;
}

/**
 * Fetch the portal user's MQTT identity. Used right before lazy-
 * loading mqtt.js so the bundle download and the identity round
 * trip happen in parallel.
 */
export async function getChatIdentity(
	token: string,
): Promise<PortalChatIdentity> {
	return chatFetch<PortalChatIdentity>('/identity', token);
}
